import { Component, Input, OnChanges, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Frequency } from '@vendasta/galaxy/frequency';
import {
  AppPrice,
  Commitment,
  Discount,
  Fee,
  FeeAmountType,
  FrequencyPrice,
  PriceDisplayOverride,
  PricingRule,
} from '@vendasta/marketplace-apps';
import { SimplePrice } from '../simple-price-display/simple-price';
import { toGalaxyFrequency } from '../utils/frequency';
import { getFeesForEdition, getLowestEditionId } from '../utils/price-display';
import { Currency } from '@galaxy/billing';

@Component({
  selector: 'billing-ui-price-display',
  templateUrl: './price-display.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PriceDisplayComponent implements OnChanges {
  Frequency = Frequency; //Frequency package
  FeeAmountType = FeeAmountType; //Fee package
  @Input() align: 'start' | 'end' | 'center' = 'start';
  @Input() editionId: string;
  @Input() appPrice: AppPrice;
  @Input() form: 'short' | 'verbose' | 'long' = 'short';
  @Input() size: 'regular' | 'large' = 'regular';
  @Input() priceType: 'generic' | 'instance' = 'generic';
  @Input() fixedTopHeight = false;

  private _currencyCode: string = Currency.USD;
  @Input()
  set currencyCode(value: string) {
    this._currencyCode = value ?? Currency.USD;
  }
  get currencyCode(): string {
    return this._currencyCode;
  }

  //price info
  topLabel: 'starting-at' | 'minimum';
  price: number;
  setupFee: number;
  frequency: Frequency;
  commitment: Commitment;
  discounts: Discount[];
  fees: Fee[];
  stairSteps: PricingRule[] = [];
  view: 'list-view' | 'card-view' = 'list-view';
  prices: SimplePrice[];

  //booleans
  contactSales = false;
  hasStairStepPricing: boolean;
  hasEditionsButNoIDGiven: boolean;
  isCardView: boolean;
  shouldShowPriceUnavailable = false;

  ngOnChanges(): void {
    this.isCardView = this.size.toLowerCase() === 'large';
    if (this.isCardView) {
      this.view = 'card-view';
    } else {
      this.view = 'list-view';
    }

    // We do the calculations after making sure the appPrice attribute was correctly subscribed by the parent component
    if (!this.appPrice) {
      this.setPriceUnavailableOverride();
      return;
    }

    this.hasEditionsButNoIDGiven = this.appPrice.billingConfiguration?.usesEditions && this.editionId === undefined;

    this.setPrice();
    this.setTopLabel();
  }

  setTopLabel(): void {
    if (this.priceType === 'instance') {
      this.topLabel = null;
      return;
    }
    if (this.appPrice.billingConfiguration?.usesVariablePricing) {
      if (this.prices) {
        this.topLabel = 'starting-at';
      } else {
        this.topLabel = 'minimum';
      }
    } else if (
      this.hasEditionsButNoIDGiven ||
      this.hasStairStepPricing ||
      this.appPrice.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_STARTING_AT
    ) {
      this.topLabel = 'starting-at';
    } else {
      this.topLabel = null;
    }
  }

  setPrice(): void {
    if (
      this.appPrice === undefined ||
      this.appPrice === null ||
      (this.priceType === 'instance' && this.hasEditionsButNoIDGiven)
    ) {
      this.setPriceUnavailableOverride();
      return;
    } else {
      this.shouldShowPriceUnavailable = false;
    }

    // Handle contact sales override
    if (this.appPrice.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES) {
      this.setContactSalesOverride();
      return;
    } else {
      this.contactSales = false;
    }
    const edition = this.getPriceEdition();
    this.setPriceForEdition(edition);
    this.setDiscountsForEdition(edition);
  }

  getPriceEdition(): string {
    let editionToBeUsed = this.editionId;
    // Set the currency to default if the item has only 1 currency
    const currencyCodes = Object.keys(
      this?.appPrice?.pricesForEditions?.[editionToBeUsed ?? '']?.pricesForCurrencies || [],
    );
    if (currencyCodes.length === 1 && currencyCodes[0] !== this.currencyCode) {
      this.currencyCode = currencyCodes[0];
    }
    // Handle specific edition price
    if (!this.appPrice.billingConfiguration?.usesEditions) {
      editionToBeUsed = '';
    } else if (this.appPrice.billingConfiguration?.usesEditions && this.editionId === undefined) {
      // Handle starting at value for editioned product
      editionToBeUsed = getLowestEditionId(this.appPrice, this.currencyCode);
    }
    return editionToBeUsed;
  }

  setPriceForEdition(editionId: string): void {
    // Prevent proceeding when the given currency doesn't match the retrieved ones
    if (
      !this?.appPrice?.pricesForEditions?.[editionId]?.pricesForCurrencies?.[this.currencyCode]?.pricesForFrequencies
    ) {
      this.setPriceUnavailableOverride();
      return;
    }

    const frequencyPrices =
      this.appPrice.pricesForEditions[editionId].pricesForCurrencies[this.currencyCode].pricesForFrequencies;
    this.calculatePrices(frequencyPrices, editionId);
  }

  private calculatePrices(frequencyPrices: FrequencyPrice[], editionId: string): void {
    // Handles multifrequency / package pricing
    const isPackage = this.hasPrefix(this.appPrice.appId, 'SOL-');
    if (isPackage || frequencyPrices.length > 1) {
      // Currently not supported for multifrequency:
      // Management Fees (will only display +Fees)
      // StairStep pricing
      // Commitments
      this.prices = [];
      let hasPrice = false;
      for (let i = 0; i < frequencyPrices.length; i++) {
        const price = frequencyPrices[i];
        let calculatedPrice = price.pricingRules[0].price;
        if (this.priceType === 'instance') {
          // Take setup fee into account, since the purchase cost considers it and we always display the setup fee separately
          const setupFee = price.setupFee || 0;
          calculatedPrice = price.instancePrice.total - setupFee;
        }

        if (!calculatedPrice) {
          if (hasPrice || i < frequencyPrices.length - 1) {
            continue;
          }
        }

        hasPrice = true;
        this.prices.push({
          price: calculatedPrice,
          frequency: toGalaxyFrequency(price.frequency),
        } as SimplePrice);
      }

      this.fees = [];
      this.setupFee = 0;
      for (const price of frequencyPrices) {
        this.setupFee += price.setupFee || 0;
        if (price.fees && price.fees.length > 0) {
          this.fees.push(...price.fees);
        }
      }

      this.hasStairStepPricing = false;
      this.stairSteps = [];
    } else {
      // Single frequency / Product pricing
      this.prices = null;
      const freqPrice = frequencyPrices[0];
      this.frequency = toGalaxyFrequency(freqPrice.frequency);
      this.price = freqPrice.pricingRules[0].price;
      this.setupFee = freqPrice.setupFee;
      this.fees = getFeesForEdition(this.appPrice, this.currencyCode, editionId);
      if (this.priceType === 'instance') {
        // Take setup fee into account, since the purchase cost considers it and we always display the setup fee separately
        const setupFee = freqPrice.setupFee || 0;
        this.price = freqPrice.instancePrice.total - setupFee;
      }
      this.commitment = freqPrice.commitment;

      if (this.priceType === 'instance' && this.size === 'large') {
        return;
      }
      this.setStairStepPricing(freqPrice);
    }
  }

  setDiscountsForEdition(editionId: string): void {
    this.discounts = [];
    // Only check for discounts if app doesn't use editions or it does and an edition id was given
    const editionPrice = this.appPrice.pricesForEditions?.[editionId];
    if (editionPrice && !this.hasEditionsButNoIDGiven) {
      this.discounts = editionPrice.discount;
    }
  }

  setContactSalesOverride(): void {
    this.price = 0;
    this.setupFee = 0;
    this.fees = [];
    this.contactSales = true;
  }

  setPriceUnavailableOverride(): void {
    this.shouldShowPriceUnavailable = true;
  }

  setStairStepPricing(freqPrice: FrequencyPrice): void {
    // Checks if it's a stairstep price
    if (freqPrice.pricingRules.length > 1 && !this.hasEditionsButNoIDGiven) {
      this.hasStairStepPricing = true;
      this.stairSteps = freqPrice.pricingRules;
    } else {
      this.hasStairStepPricing = false;
      this.stairSteps = [];
    }
  }

  hasPrefix(str: string, prefix: string): boolean {
    return str.startsWith(prefix);
  }
}
